import { useCallback } from 'react';
import { useLocation } from '@reach/router';
import { navigate as gatsbyNavigate } from 'gatsby';
import { useDispatch, useSelector } from 'react-redux';
import history from 'history/browser';
import { pathToRegex } from '#/shared/utils/navigate';
import { navigateBack as navigateBackAction } from '#/shared/slices/history';
import ROUTES from '#/constants/routes';
import useBasePath from '#/shared/hooks/useBasePath';

export default function useNavigate() {
  const basePath = useBasePath();

  const navigate = useCallback(
    (route, ...args) =>
      gatsbyNavigate(route.navigate(...args.slice(0, route.navigate.length)), {
        // extra arguments are passed via location state
        state: {
          extraArgs: args.slice(route.navigate.length, args.length),
        },
      }),
    []
  );

  /**
   * The best practice method of navigating between internal pages is via
   * `gatsby.Link` or `gatsby.navigate`, but as of Gatsby 4 those methods don't
   * allow a page to be opened in a new tab.  This function provides that but
   * is less performant than the aforementioned approaches
   */
  const navigateInNewTab = useCallback(
    route => {
      window.open(
        `${window.origin}${basePath}${route.navigate()}`,
        '_blank',
        'noopener'
      );
    },
    [basePath]
  );

  /**
   * Navigate to stop detail page and encode the supplied route-stop sign keys
   * as querystring params. The params are intended to be used reveal arrivals
   * associated with the supplied signs
   *
   * @param selectedStopId {string} Unique ID for transit stop
   * @param signKeys {string[]} Array of unique identifiers for route-stop
   * signs; these signs map to subsets of the numbered transit routes
   */
  const navigateToStopAndSign = useCallback(
    (selectedStopId, signKeys) => {
      return navigate(
        ROUTES.stopById,
        `${selectedStopId}${
          signKeys ? `?${signKeys.map(s => `sign=${s}`).join('&')}` : ''
        }`
      );
    },
    [navigate]
  );

  const location = useLocation();
  const { previousPathname } = useSelector(s => s.shared.history);
  const dispatch = useDispatch();

  /**
   * Use matching similar to that done by the <Router> component, but outside of
   * the render cycle.  Matches a router path to the location pathnames derived
   * from it: e.g. `/route/:route_id` matches `/route/100` and `/route/200`
   *
   * @param [routeArray] {Object[]} Array of route objects as defined in the route
   * constants file, must have a property `path` that holds a valid value for
   * the router's `path` prop.
   * @param [pathname] {string} Path component of application URL as returned by
   * the router, default to the current location of the app
   * @return {boolean} Returns `true` if any route paths match the pathname
   */
  const matchPath = useCallback(
    (routeArray = [], pathname = location.pathname) => {
      if (!pathname) {
        return false;
      }
      return routeArray.some(
        route => !!pathToRegex(route.path, basePath).exec(pathname)
      );
    },
    [basePath, location.pathname]
  );

  /**
   * Conditionally generate a function that navigates the user one step
   * backwards in the history of the paths they've visited within the app. If
   * going back in the history stack takes the user out of the app return a
   * function that navigates to the default route, if supplied, otherwise null.
   * If the previously visited path matches an item in `restrictedRoutes` return
   * null
   *
   * @param [restrictedRoutes] {Object[]} Array of route objects as defined in
   * this project's route constants file
   * @param [defaultRoute] {Object} When supplied, the returned function will
   * navigate to this route if the user has no previously visited routes in the
   * app session
   * @return {function|null}
   */

  const generateNavigateBack = (restrictedRoutes, defaultRoute = null) => {
    if (!previousPathname) {
      return defaultRoute ? () => navigate(defaultRoute) : null;
    }

    if (matchPath(restrictedRoutes, previousPathname)) {
      return null;
    }

    return () => {
      // this action sets a node in the redux state that allows the
      // `HistoryManager` component to properly handle a move backwards
      dispatch(navigateBackAction());
      history.back();
    };
  };

  /**
   * Conditionally generate a function that navigates the user to the root
   * view.  If the previous path matches a restricted route and the current view
   * is not the first one visited by the user, return null. The second condition
   * ensures that one of `generateNavigateBack` and this function will always
   * return at least on function between them when invoked on the same render;
   * see that function's docstring for details on the `restrictedRoutes` param
   *
   * @param [restrictedRoutes] {Object[]}
   * @return {function|null}
   */
  const generateNavigateClose = restrictedRoutes => {
    if (matchPath(restrictedRoutes, previousPathname)) {
      return null;
    }

    return () => navigate(ROUTES.home);
  };

  return {
    navigate,
    navigateInNewTab,
    navigateToStopAndSign,
    generateNavigateBack,
    generateNavigateClose,
    matchPath,
  };
}
